// Portal-only theme system, deliberately separate from lib/theme.tsx.
//
// Why a separate provider:
//   - Storage key namespace: 'orkestra-portal-theme' vs the staff
//     app's 'orkestra-theme'. A staff member using the portal in the
//     same browser must not clobber their app preference and vice
//     versa.
//   - Scope: applies data-theme to a portal wrapper element, NOT
//     document.documentElement. tokens.css uses generic attribute
//     selectors ([data-theme="light"] { ... }) and CSS custom
//     properties inherit through the DOM, so the wrapping div's
//     descendants — and only those — pick up the portal's tokens.
//     The staff app rendering on a different route (impossible in
//     practice but conceptually) would still inherit <html>'s
//     data-theme.
//   - No profile sync: portal users have profile === null. The staff
//     provider's profile.appearance sync branch is already a no-op for
//     them; this provider drops it entirely.
//
// UX model: a single sun/moon icon button cycles light ↔ dark with
// 'system' as the unset default (follows prefers-color-scheme). The
// icon shows what clicking would SWITCH TO (sun when currently dark,
// moon when currently light) — the macOS / iOS convention.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { MotionConfig } from 'framer-motion'
import { deriveBrandOverrides } from '@/lib/portalBrand'

export type PortalTheme = 'light' | 'dark' | 'system'
export type ResolvedPortalTheme = 'light' | 'dark'

const STORAGE_KEY = 'orkestra-portal-theme'

interface Ctx {
  theme: PortalTheme
  resolvedTheme: ResolvedPortalTheme
  setTheme: (next: PortalTheme) => void
  toggle: () => void
  // Brand color flows in once the workspace data is loaded inside
  // PortalLayout. Provider applies the derived accent-family overrides
  // on its own wrapping div as inline style. Pre-auth pages stay
  // null → tokens.css default purple.
  brandColor: string | null
  setBrandColor: (next: string | null) => void
}

const PortalThemeContext = createContext<Ctx | null>(null)

function readStored(): PortalTheme {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw
  } catch { /* SSR / private mode */ }
  return 'system'
}

function resolveTheme(t: PortalTheme): ResolvedPortalTheme {
  if (t !== 'system') return t
  const prefersDark = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-color-scheme: dark)').matches
  return prefersDark ? 'dark' : 'light'
}

export function PortalThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<PortalTheme>(() => readStored())
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedPortalTheme>(() => resolveTheme(readStored()))
  const [brandColor, setBrandColor] = useState<string | null>(null)

  // Re-resolve whenever the picker changes.
  useEffect(() => {
    setResolvedTheme(resolveTheme(theme))
  }, [theme])

  // Live OS preference listener — only meaningful while theme==='system'.
  useEffect(() => {
    if (theme !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => setResolvedTheme(mq.matches ? 'dark' : 'light')
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [theme])

  const setTheme = useCallback((next: PortalTheme) => {
    setThemeState(next)
    try { localStorage.setItem(STORAGE_KEY, next) } catch { /* ignore */ }
  }, [])

  const toggle = useCallback(() => {
    // Cycle: any state → opposite of current resolved. After first
    // toggle we always carry an explicit 'light' or 'dark', never
    // re-enter 'system' (that's the default-on-fresh-storage state).
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')
  }, [resolvedTheme, setTheme])

  const value = useMemo<Ctx>(
    () => ({ theme, resolvedTheme, setTheme, toggle, brandColor, setBrandColor }),
    [theme, resolvedTheme, setTheme, toggle, brandColor],
  )

  // Brand-derived accent overrides. Spread onto the wrapper's style;
  // empty object when brand is null/malformed/unworkable → tokens.css
  // default purple stands. Recomputes on theme flip because the
  // alpha-blend and text-shift values are theme-dependent.
  const brandStyle = useMemo(
    () => deriveBrandOverrides(brandColor, resolvedTheme),
    [brandColor, resolvedTheme],
  )

  return (
    <PortalThemeContext.Provider value={value}>
      {/* MotionConfig at the provider so every framer-motion descendant
          honors prefers-reduced-motion automatically — "user" defers to
          the OS setting. */}
      <MotionConfig reducedMotion="user">
        {/* The data-theme wrapper. min-height keeps full-viewport pages
            (PortalLogin / PortalSetPassword) painting their --canvas
            color across the whole screen even when content is short.
            Brand overrides ride this same element so they cascade
            alongside the theme tokens. */}
        <div data-theme={resolvedTheme} style={{ minHeight: '100vh', ...brandStyle }}>
          {children}
        </div>
      </MotionConfig>
    </PortalThemeContext.Provider>
  )
}

export function usePortalTheme(): Ctx {
  const ctx = useContext(PortalThemeContext)
  if (!ctx) throw new Error('usePortalTheme must be used inside <PortalThemeProvider>')
  return ctx
}
